// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/stayNight.ts
// 🔧 PURPOSE: A che NOTTE del soggiorno siamo, sui giorni di Roma. Si
//             contano le notti, non i giorni, come il televisore e il
//             tablet in casa: prima l'app diceva "Giorno 1 di 2" il giorno
//             dell'arrivo di una notte sola.
//
//             Il giorno dell'arrivo è la notte 1; la vigilia della partenza
//             è l'"Ultima notte"; il giorno della partenza non ha numero
//             ("Giorno della partenza"). Con una notte sola il giorno
//             dell'arrivo è "Una notte con noi".
//
//             3 notti, 26 → 29: 26 "Notte 1 di 3", 27 "Notte 2 di 3",
//                               28 "Ultima notte", 29 "Giorno della partenza".
//             1 notte, 26 → 27: 26 "Una notte con noi · partenza domani",
//                               27 "Giorno della partenza".
//
//             Una funzione pura: la usano la Home (lib/homeState), Il mio
//             soggiorno (pages/mystay/stayPhase) ed Esplora per la stessa riga.
// ============================================

import { dayKeyDiff, romeDayKey } from './romeDay';

export type StayNight =
  /** Una notte che non è l'ultima: "Notte 2 di 3". */
  | { kind: 'night'; night: number; total: number }
  /** La vigilia della partenza, con più notti: "Ultima notte". */
  | { kind: 'lastNight'; total: number }
  /** Il giorno dell'arrivo di una notte sola: "Una notte con noi". */
  | { kind: 'oneNight' }
  /** Il giorno della partenza: "Giorno della partenza". */
  | { kind: 'departure' };

/** "AAAA-MM-GG" resta com'è (è già il giorno di Roma); un ISO si porta al giorno di Roma. */
const dayKey = (value: string | null | undefined): string => {
  if (!value) return '';
  const s = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : romeDayKey(s);
};

/**
 * La notte di oggi (`today`, giorno di Roma "AAAA-MM-GG") in un soggiorno
 * dall'arrivo alla partenza (ISO o "AAAA-MM-GG"). null prima dell'arrivo,
 * dopo la partenza, senza date o senza notti.
 */
export function stayNightOf(
  checkIn: string | null | undefined,
  checkOut: string | null | undefined,
  today: string,
): StayNight | null {
  const inKey = dayKey(checkIn);
  const outKey = dayKey(checkOut);
  if (!inKey || !outKey || !today) return null;
  const total = dayKeyDiff(outKey, inKey);
  if (total === null || total < 1) return null;
  if (today === outKey) return { kind: 'departure' };
  const index = dayKeyDiff(today, inKey);
  if (index === null || index < 0 || index >= total) return null;
  if (total === 1) return { kind: 'oneNight' };
  if (index === total - 1) return { kind: 'lastNight', total };
  return { kind: 'night', night: index + 1, total };
}
